import React, { FC, PropsWithChildren } from 'react';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { PathGeneratorContextProvider, coreRouterApi, createRoutesDict } from '@cp/entities/clientWebRoutes';
import { ClientWebRouteViewModel } from '@common/model/meta/clientWebRouteViewModel';
import { ROUTES_DEFAULT_CITY_ALIAS } from '@common/const/routes/defaultCity';
import { renderRoutes } from 'react-router-config';
import { useIsEmbedded } from 'src/shared/mediaQueries';
import { useCookieStorageCleaner } from 'src/app/shared/hooks/useClientStorageCleaner';
import { useScrollToTop } from 'src/app/shared/hooks/useScrollToTop';
import { useRedirectToClientCity } from 'src/app/shared/hooks/useRedirectToClientCity';
import { NotificationsSubscribeReminder } from 'src/features/notifications';
import { createRoutesConfig } from 'src/app/routes/config';
import { createUseAppPathGenerator } from 'src/app/useAppPathGenerator';
import { pageIdToRouteData } from 'src/app/routes/page';
import { ModalProvider } from 'src/app/shared/components/modalProvider';
import { AppModalManager } from '../modal';

const Effects: React.FC = () => {
  const isLoggedIn = useIsLoggedIn();
  const isEmbedded = useIsEmbedded();

  useCookieStorageCleaner();
  useScrollToTop();
  useRedirectToClientCity();

  const effects = isLoggedIn && isEmbedded ? <NotificationsSubscribeReminder /> : null;

  return effects;
};

export const AppProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data: routesResponse } = coreRouterApi.useGetClientWebRoutesQuery();
  const routesRef = React.useRef<ClientWebRouteViewModel[]>([]);

  const { routesConfig, routesDict } = React.useMemo(() => {
    if (routesResponse) {
      routesRef.current = routesResponse;
    }

    return {
      routesConfig: createRoutesConfig(routesRef.current, { defaultCityAlias: ROUTES_DEFAULT_CITY_ALIAS, pageIdToRouteData }),
      routesDict: createRoutesDict(routesRef.current),
    };
  }, [routesResponse]);

  const useAppPathGenerator = createUseAppPathGenerator(routesDict);

  return (
    <PathGeneratorContextProvider value={useAppPathGenerator}>
      <Effects />

      {children ? children : renderRoutes([routesConfig])}

      <ModalProvider />
      <AppModalManager />
    </PathGeneratorContextProvider>
  );
};
